import { supabase } from "@/integrations/supabase/client";
import type { CollectionRow, CollectionUpsert } from "@/lib/collections";

// All Supabase I/O for collections. The public site reads its own rows plus
// the global ones (tenant_id NULL); the admin reads everything.

export async function fetchCollectionRows(
  tenantId: string,
  opts: { includeUnpublished?: boolean } = {},
): Promise<CollectionRow[]> {
  let q = supabase
    .from("collections")
    .select("*")
    .or(`tenant_id.is.null,tenant_id.eq.${tenantId}`)
    .order("sort_order")
    .order("title");
  if (!opts.includeUnpublished) q = q.eq("published", true);
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

export async function fetchAllCollectionRows(): Promise<CollectionRow[]> {
  const { data, error } = await supabase.from("collections").select("*").order("sort_order").order("title");
  if (error) throw error;
  return data ?? [];
}

/** Insert or replace the row for (tenant_id, slug). */
export async function upsertCollectionRow(row: CollectionUpsert): Promise<CollectionRow> {
  const { data, error } = await supabase
    .from("collections")
    .upsert(row, { onConflict: "tenant_id,slug" })
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

/**
 * Save an existing row by id. Editing a collection's address must move that
 * row, not upsert on (tenant_id, slug): that left the old page live and could
 * overwrite another collection that already had the new address.
 */
export async function updateCollectionRow(id: string, row: CollectionUpsert): Promise<CollectionRow> {
  const { data, error } = await supabase.from("collections").update(row).eq("id", id).select("*").single();
  if (error) throw error;
  return data;
}

export async function deleteCollectionRow(id: string): Promise<void> {
  const { error } = await supabase.from("collections").delete().eq("id", id);
  if (error) throw error;
}

/** Postgres says the table is missing (SQL not pasted yet), the admin banner case. */
export const isMissingCollectionsTable = (e: unknown): boolean => {
  const msg = e instanceof Error ? e.message : typeof e === "object" && e && "message" in e ? String((e as { message: unknown }).message) : String(e);
  const code = typeof e === "object" && e && "code" in e ? String((e as { code: unknown }).code) : "";
  return code === "42P01" || /relation .*collections.* does not exist|Could not find the table/i.test(msg);
};
